import { useEffect, useRef, useState } from "react"
import { PencilSquareIcon } from "@heroicons/react/24/outline"
import { Scrollbars } from 'react-custom-scrollbars-2';
import { Textarea } from "@heroui/react"
import PropTypes from "prop-types"

const TeacherCommentsScrollbars = ({ children }) => (
  <Scrollbars
    autoHeight 
    autoHeightMax={200} 
    autoHide 
    renderThumbVertical={({ style, ...props }) => (
      <div
        {...props}
        style={{ ...style, backgroundColor: '#c1c1c1', borderRadius: '5px' }}
      />
    )}
    renderTrackVertical={({ style, ...props }) => (
      <div
        {...props}
        style={{ ...style, borderRadius: '5px', width: '6px', right: '2px', bottom: '8px', top: '8px' }}
      />
    )}
  >
    {children}
  </Scrollbars>
)

const ParentCommentsScrollbars = ({ children }) => (
  <Scrollbars autoHeight autoHeightMax={100} autoHide>
    { children }
  </Scrollbars>
)

const CompactComments = ({
  id,
  classNames = {}, 
  handleUpdateTeacherComment, 
  initialTeacherComment = null, 
  parentComment = null, 
  parentCommentScrolls = false,
  showEditButton = false,
  showGuardianComments = true,
  teacherCommentScrolls = false,
}) => {
  const [editTeacherComments, setEditTeacherComments] = useState(!showEditButton)
  const [teacherComment, setTeacherComment] = useState(initialTeacherComment || "")
  const textareaRef = useRef(null)

  // Set focus and move cursor to the end
  useEffect(() => {
    if (editTeacherComments) {
      textareaRef.current.focus()
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [editTeacherComments])
  
  // Refresh the comment when the initialTeacherComment changes
  useEffect(() => {
    setTeacherComment(initialTeacherComment)
  }, [id, initialTeacherComment])

  const handleSaveComments = () => {
    handleUpdateTeacherComment(teacherComment)

    if (showEditButton) {
      setEditTeacherComments(false)
    }
  }
   
  const teacherTextArea = (
    <Textarea
      ref={textareaRef}
      classNames={classNames}
      className={`w-full`} 
      placeholder="Add your notes here" 
      maxRows="100" 
      value={teacherComment || ""} 
      onValueChange={(value) => setTeacherComment(value)}
      onBlur={() => handleSaveComments()} 
    />
  )

  const parentComments = (
    <p className="overflow-hidden text-sm">
      {parentComment
        ? parentComment.split("\n").map((line, index) => (
            <span className="block py-0.5" key={index}>{line}</span>
          ))
        : "Guardian left no comments."}
    </p>
  )

  return (
    <div id="notes" className={classNames.container || ""}>
      <div id="teacher-notes" className="mb-4">
        <div className="flex justify-between">
          <p className={`mb-2 text-sm text-content2-foreground ${classNames.label || ''}`}>Your Notes</p>
          {showEditButton && <PencilSquareIcon className="w-8 h-8 cursor-pointer hover:text-gray-600" onClick={() => setEditTeacherComments(true)} />}
        </div>
        
        {editTeacherComments ? (
          teacherCommentScrolls ? <TeacherCommentsScrollbars>{teacherTextArea}</TeacherCommentsScrollbars> : teacherTextArea
        ) : (
          <p className="overflow-hidden text-sm" onClick={() => setEditTeacherComments(true)}>
            {teacherComment
              ? teacherComment.split("\n").map((line, index) => (
                <span className="block py-0.5" key={index}>{line}</span>
              ))
              : "Add your notes here"}
          </p>
        )}
      </div>
      
      { showGuardianComments && (
        <div id="parent-notes">
          <p className={`mb-2 text-sm text-content2-foreground ${classNames.label || ''}`}>Guardian Notes</p>

          {parentCommentScrolls ? <ParentCommentsScrollbars>{parentComments}</ParentCommentsScrollbars> : parentComments}
        </div>
      )}
    </div>
  )
}

CompactComments.propTypes = {
  id: PropTypes.string,
  classNames: PropTypes.object,
  handleUpdateTeacherComment: PropTypes.func.isRequired,
  initialTeacherComment: PropTypes.string,
  parentComment: PropTypes.string,
  parentCommentScrolls: PropTypes.bool,
  showEditButton: PropTypes.bool,
  showGuardianComments: PropTypes.bool,
  teacherCommentScrolls: PropTypes.bool,
}

export default CompactComments